<template>
  <v-container fluid>
    <section class="ps-overview">
      <div class="row align-items-end ps-overview-header">
        <div class="col-xs-12 col-md-5 col-lg-5">
          <div class="ps-overview-eyebrow">{{ $t("store_performance") }}</div>
          <h1 class="ps-overview-title">{{ periodTitle }}</h1>
        </div>
        <div
          class="col-xs-12 col-md-7 col-lg-7 d-flex justify-md-end align-center flex-wrap ps-header-controls"
        >
          <v-btn-toggle
            v-model="period"
            mandatory
            dense
            class="ps-period-toggle"
          >
            <v-btn small value="week">{{ $t("period_week") }}</v-btn>
            <v-btn small value="month">{{ $t("month") }}</v-btn>
            <v-btn small value="quarter">{{ $t("period_quarter") }}</v-btn>
            <v-btn small value="year">{{ $t("year") }}</v-btn>
          </v-btn-toggle>
          <v-select
            v-if="showYear"
            v-model="revenueSummaryYear"
            :items="revenueYearOptions"
            :label="$t('year')"
            dense
            outlined
            hide-details
            class="ps-year-select"
          />
          <v-btn
            small
            outlined
            class="ps-export-btn"
            :disabled="!series1.length"
            @click="exportCsv"
          >
            <v-icon left small>fal fa-download</v-icon>
            {{ $t("export") }}
          </v-btn>
        </div>
      </div>

      <div class="row">
        <template v-if="top_summaries.length">
          <div
            class="col-xs-12 col-sm-6 col-md-3 col-lg-3"
            v-for="(item, index) in kpiCards"
            :key="index"
          >
            <v-card class="ps-card-soft ps-kpi-card">
              <v-card-text>
                <div class="d-flex align-center mb-2">
                  <div class="ps-kpi-icon mr-3" :style="kpiIconStyle(item)">
                    <v-icon small>{{ item.icon }}</v-icon>
                  </div>
                  <div class="ps-kpi-meta">
                    <div class="ps-kpi-label">{{ $t(item.text) }}</div>
                    <div class="ps-kpi-desc">{{ $t(item.description) }}</div>
                  </div>
                </div>
                <div
                  class="ps-kpi-value"
                  :class="{ 'ps-dark-mode-value': $vuetify.theme.dark }"
                >
                  {{ item.value | NumberFormat }}
                </div>
                <svg
                  v-if="item.spark"
                  class="ps-kpi-spark"
                  viewBox="0 0 100 24"
                  preserveAspectRatio="none"
                >
                  <path :d="item.spark.area" :fill="item.spark.fill"></path>
                  <path
                    :d="item.spark.line"
                    fill="none"
                    :stroke="item.spark.stroke"
                    stroke-width="1.6"
                    stroke-linejoin="round"
                    vector-effect="non-scaling-stroke"
                  ></path>
                </svg>
                <div v-else class="ps-kpi-spark-placeholder"></div>
              </v-card-text>
            </v-card>
          </div>
        </template>
        <div
          v-else
          class="col-xs-12 col-sm-6 col-md-3 col-lg-3"
          v-for="(item, index) in 4"
          :key="index"
        >
          <v-card class="ps-card-soft">
            <v-card-text>
              <v-skeleton-loader
                type="table-heading, list-item-avatar-three-line"
              />
            </v-card-text>
          </v-card>
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-7 col-lg-7">
          <v-card class="ps-card-soft ps-chart-card">
            <v-card-title class="d-flex align-center flex-wrap pb-1">
              <span>{{ $t("profit") }} &amp; {{ $t("revenue") }}</span>
              <v-spacer />
              <div class="ps-legend">
                <span class="ps-legend-item">
                  <span class="ps-legend-dot ps-legend-dot--profit"></span>
                  {{ $t("profit") }}
                </span>
                <span class="ps-legend-item">
                  <span class="ps-legend-dot ps-legend-dot--revenue"></span>
                  {{ $t("revenue") }}
                </span>
              </div>
            </v-card-title>

            <v-card-text>
              <div class="ps-chart-summary">
                <span
                  class="ps-chart-summary-value"
                  :class="{ 'ps-dark-mode-value': $vuetify.theme.dark }"
                >
                  {{ summaryTotals.total_profit | NumberFormat }}
                </span>
                <span class="ps-chart-summary-note">{{ profitNote }}</span>
              </div>
              <div id="chart" v-if="series1.length > 0">
                <apexchart
                  width="100%"
                  height="260"
                  type="line"
                  :options="option"
                  :series="series1"
                ></apexchart>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div class="col-xs-12 col-sm-12 col-md-5 col-lg-5">
          <v-card class="ps-card-soft ps-chart-card">
            <v-card-title class="d-flex align-center flex-wrap pb-1">
              <span>{{ $t("total_sale") }}</span>
              <v-spacer />
              <span class="ps-muted-note">{{ $t("orders_per_month") }}</span>
            </v-card-title>
            <v-card-text>
              <div class="ps-chart-summary">
                <span
                  class="ps-chart-summary-value"
                  :class="{ 'ps-dark-mode-value': $vuetify.theme.dark }"
                >
                  {{ totalOrders | NumberFormat }}
                </span>
                <span class="ps-chart-summary-note">{{ busiestNote }}</span>
              </div>
              <apexchart
                v-if="series2.length > 0"
                type="bar"
                height="230"
                :options="chartOptions2"
                :series="series2"
              ></apexchart>
            </v-card-text>
          </v-card>
        </div>
        <div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
          <v-card class="ps-card-soft ps-best-card">
            <v-card-title class="d-flex align-center flex-wrap ps-best-head">
              <span class="ps-best-title">
                <span class="ps-title-tick"></span>
                {{ $t("best_sell") }}
              </span>
              <span v-if="top_sells.length" class="ps-best-note">{{
                $t("top_products_note", {
                  shown: topSellShown,
                  total: top_sells.length,
                })
              }}</span>
              <v-spacer />
              <v-btn-toggle
                v-model="sortMode"
                mandatory
                dense
                class="rounded-lg mr-4 ps-sort-toggle"
              >
                <v-btn small value="units">{{ $t("sort_units") }}</v-btn>
                <v-btn small value="value">{{ $t("sort_value") }}</v-btn>
              </v-btn-toggle>
              <router-link :to="{ name: 'products' }" class="ps-view-all">
                {{ $t("view_all_products") }}
              </router-link>
            </v-card-title>

            <v-card-text>
              <div class="ps-best-table">
                <div class="ps-best-row ps-best-row--head">
                  <span>{{ $t("id") }}</span>
                  <span>{{ $t("image") }}</span>
                  <span>{{ $t("product_name") }}</span>
                  <span class="ps-best-col--right">{{ $t("price") }}</span>
                  <span class="ps-best-col--right">{{
                    $t("units_sold")
                  }}</span>
                  <span class="ps-best-col--right">{{
                    $t("total_price")
                  }}</span>
                </div>

                <div
                  v-for="(item, index) in sortedTopSells.slice(0, 6)"
                  :key="item.id"
                  class="ps-best-row"
                >
                  <span class="ps-best-id">{{ index + 1 }}</span>
                  <v-avatar tile size="34" class="rounded-lg ps-best-thumb">
                    <app-img :img="item.img" />
                  </v-avatar>
                  <div class="ps-best-name-cell">
                    <span class="ps-product-name">{{ item.name }}</span>
                    <span
                      v-if="categoryName(item)"
                      class="ps-product-category"
                      >{{ categoryName(item) }}</span
                    >
                  </div>
                  <span class="ps-best-col--right ps-best-price">{{
                    priceWithCurrency(item.price_sale, item.currency)
                  }}</span>
                  <div class="ps-units-cell">
                    <span class="ps-units-bar-track">
                      <span
                        class="ps-units-bar-fill"
                        :style="{ width: unitsBarWidth(item) }"
                      ></span>
                    </span>
                    <span class="ps-units-value">{{
                      item.histories_sum_qty | NumberFormat
                    }}</span>
                  </div>
                  <span class="ps-best-col--right ps-best-total">{{
                    item.histories_sum_total_price | NumberFormat
                  }}</span>
                </div>

                <div v-if="!sortedTopSells.length" class="ps-best-empty">
                  ບໍ່ມີຂໍ້ມູນ
                </div>
              </div>
              <div class="ps-table-foot">
                <span>{{ tableFoot }}</span>
                <span class="ps-table-foot-hint">{{
                  $t("total_price_hint")
                }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </section>
  </v-container>
</template>

<script>
import ApexCharts from "vue-apexcharts";
import { myMixin } from "@/mixin/main";

export default {
  components: {
    apexchart: ApexCharts,
  },
  mixins: [myMixin],

  data() {
    return {
      option: {},
      series1: [],
      chartOptions2: {},
      series2: [],
      top_summaries: [],
      top_sells: [],
      revenueSummaryYear: new Date().getFullYear(),
      profitSeries: [],
      revenueSeries: [],
      countSeries: [],
      monthLabels: [],
      summaryTotals: { total_profit: 0, total_revenue: 0 },
      sortMode: "units",
      period: "year",
    };
  },
  mounted() {
    this.fetchAll();
  },
  computed: {
    selectedRevenueYear() {
      const n = parseInt(this.revenueSummaryYear, 10);
      return Number.isFinite(n) ? n : new Date().getFullYear();
    },
    revenueYearOptions() {
      const current = new Date().getFullYear();
      const span = 15;
      const list = [];
      for (let y = current; y >= current - span; y -= 1) {
        list.push(y);
      }
      return list;
    },
    periodTitle() {
      if (this.period === "week") return this.$t("title_last_7_days");
      if (this.period === "month") return this.$t("title_last_30_days");
      if (this.period === "quarter") {
        return this.$t("title_quarter", { year: this.revenueSummaryYear });
      }
      return this.$t("year_to_date", { year: this.revenueSummaryYear });
    },
    showYear() {
      return this.period === "year" || this.period === "quarter";
    },
    profitNote() {
      return this.showYear
        ? this.$t("profit_to_date", { year: this.revenueSummaryYear })
        : this.$t("profit_period");
    },
    kpiCards() {
      const seriesMap = {
        profit: this.profitSeries,
        revenue: this.revenueSeries,
        order: this.countSeries,
      };
      return this.top_summaries.map((item) => {
        const series = seriesMap[item.text];
        const spark =
          series && series.length ? this.buildSpark(series, item.color) : null;
        return { ...item, spark };
      });
    },
    totalOrders() {
      return this.countSeries.reduce((sum, v) => sum + (v || 0), 0);
    },
    busiestIndex() {
      let maxV = -1;
      let idx = -1;
      this.countSeries.forEach((v, i) => {
        if (v > maxV) {
          maxV = v;
          idx = i;
        }
      });
      return maxV > 0 ? idx : -1;
    },
    busiestNote() {
      if (this.busiestIndex < 0) return "";
      const label = this.monthLabels[this.busiestIndex] || "";
      const count = this.countSeries[this.busiestIndex];
      if (this.period === "year") {
        return this.$t("busiest_month", { month: label, count });
      }
      return this.$t("peak_note", { label, count });
    },
    maxUnitsSold() {
      return (
        this.sortedTopSells.slice(0, 6).reduce(
          (max, p) => Math.max(max, p.histories_sum_qty || 0),
          0,
        ) || 1
      );
    },
    sortedTopSells() {
      const list = this.top_sells.slice();
      if (this.sortMode === "value") {
        return list.sort(
          (a, b) =>
            (b.histories_sum_total_price || 0) -
            (a.histories_sum_total_price || 0),
        );
      }
      return list.sort(
        (a, b) => (b.histories_sum_qty || 0) - (a.histories_sum_qty || 0),
      );
    },
    tableFoot() {
      const key =
        this.sortMode === "value" ? "sorted_by_value" : "sorted_by_units";
      return this.$t(key, { year: this.revenueSummaryYear });
    },
    topSellShown() {
      return Math.min(6, this.sortedTopSells.length);
    },
  },
  watch: {
    revenueSummaryYear() {
      this.fetchAll();
    },
    period() {
      this.fetchAll();
    },
  },
  methods: {
    periodParams() {
      return { period: this.period, year: this.selectedRevenueYear };
    },
    fetchAll() {
      this.top_summary();
      this.summary_revenue();
      this.top_selling();
    },
    exportCsv() {
      const header = [
        this.period === "year" ? this.$t("month") : this.$t("date"),
        this.$t("orders"),
        this.$t("profit"),
        this.$t("revenue"),
      ];
      const rows = [header];
      this.monthLabels.forEach((label, i) => {
        rows.push([
          label,
          this.countSeries[i] || 0,
          this.profitSeries[i] || 0,
          this.revenueSeries[i] || 0,
        ]);
      });
      rows.push([
        this.$t("total"),
        this.totalOrders,
        this.summaryTotals.total_profit,
        this.summaryTotals.total_revenue,
      ]);
      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");
      const blob = new Blob(["﻿" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `overview_${this.period}_${this.revenueSummaryYear}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async top_summary() {
      try {
        const { data } = await this.$http.get("/top_summary", {
          params: this.periodParams(),
        });
        this.top_summaries = data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async summary_revenue() {
      try {
        const { data } = await this.$http.get("/summary_revenue", {
          params: this.periodParams(),
        });
        const rows = data.data?.data || [];
        const profitArr = Help.pluck(rows, "price_profit");
        const revenueArr = Help.pluck(rows, "price_revenue");
        const countArr = Help.pluck(rows, "count");
        const labels = Help.pluck(rows, "month");

        this.profitSeries = profitArr;
        this.revenueSeries = revenueArr;
        this.countSeries = countArr;
        this.monthLabels = labels;
        this.summaryTotals = {
          total_profit: data.data.total_profit || 0,
          total_revenue: data.data.total_revenue || 0,
        };

        this.series1 = [
          { name: this.$t("profit"), type: "area", data: profitArr },
          { name: this.$t("revenue"), type: "line", data: revenueArr },
        ];
        this.option = {
          chart: {
            type: "line",
            fontFamily: "NotoSansLaoUI",
            zoom: {
              enabled: false,
            },
            toolbar: {
              show: false,
            },
          },
          colors: ["#5b39c9", "#94a3b8"],
          stroke: {
            curve: "smooth",
            width: [2.5, 2],
            dashArray: [0, 6],
          },
          fill: {
            type: ["gradient", "solid"],
            opacity: [0.35, 0],
            gradient: {
              shadeIntensity: 1,
              opacityFrom: 0.35,
              opacityTo: 0.02,
              stops: [0, 90, 100],
            },
          },
          dataLabels: {
            enabled: false,
          },
          labels: labels,
          legend: {
            show: false,
          },
          yaxis: {
            opposite: true,
            labels: {
              formatter: (val) => {
                return this.formatNumber(val);
              },
            },
          },
          tooltip: {
            y: {
              formatter: (val) => {
                return this.formatNumber(val);
              },
            },
          },
        };

        this.series2 = [
          {
            name: this.$t("total_sale"),
            data: countArr,
          },
        ];
        this.chartOptions2 = {
          colors: ["#5b39c9"],
          chart: {
            type: "bar",
            fontFamily: "NotoSansLaoUI",
            zoom: {
              enabled: false,
            },
            toolbar: {
              show: false,
            },
          },
          plotOptions: {
            bar: {
              borderRadius: 4,
              columnWidth: "55%",
              dataLabels: {
                position: "center",
              },
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (val) => {
              if (!val) return "";
              return this.formatNumber(val);
            },
            style: {
              colors: ["#ffffff"],
              fontSize: "12px",
              fontWeight: 600,
            },
          },
          xaxis: {
            categories: labels,
          },
          yaxis: {
            opposite: true,
            labels: {
              formatter: (val) => {
                return this.formatNumber(val);
              },
            },
          },
          tooltip: {
            y: {
              formatter: (val) => {
                return this.formatNumber(val);
              },
            },
          },
        };
      } catch (error) {
        console.log(error);
      }
    },
    async top_selling() {
      try {
        const { data } = await this.$http.get("/top_selling", {
          params: { ...this.periodParams(), limit: 200 },
        });
        this.top_sells = data.data;
      } catch (error) {
        console.log(error);
      }
    },
    buildSpark(series, color) {
      const max = Math.max(...series);
      const min = Math.min(...series, 0);
      const range = max - min || 1;
      const step = series.length > 1 ? 100 / (series.length - 1) : 100;
      const pts = series.map((v, i) => [
        i * step,
        22 - ((v - min) / range) * 19,
      ]);
      const line = pts
        .map(
          (p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1),
        )
        .join(" ");
      return {
        line,
        area: line + " L100 24 L0 24 Z",
        stroke: color,
        fill: this.hexToRgba(color, 0.16),
      };
    },
    hexToRgba(hex, alpha) {
      const h = (hex || "#8c9eff").replace("#", "");
      const full =
        h.length === 3
          ? h
              .split("")
              .map((c) => c + c)
              .join("")
          : h;
      const num = parseInt(full, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    kpiIconStyle(item) {
      return {
        background: this.hexToRgba(item.color, 0.14),
        color: item.color,
        borderColor: this.hexToRgba(item.color, 0.4),
      };
    },
    categoryName(item) {
      const cat = item.category;
      if (!cat) return "";
      if (this.$i18n.locale === "en" && cat.en_name) return cat.en_name;
      return cat.la_name || cat.en_name || "";
    },
    unitsBarWidth(item) {
      const pct = ((item.histories_sum_qty || 0) / this.maxUnitsSold) * 100;
      return `${Math.max(pct, 4)}%`;
    },
  },
};
</script>

<style scoped>
/* Hallmark · redesign: header + best-sell only · tokens: light + dark (theme--dark)
 * KPI cards, chart cards and data layer intentionally untouched. */

/* Theme-aware tokens scoped to this page */
.ps-overview {
  --ps-ov-ink: #141021;
  --ps-ov-muted: #64748b;
  --ps-ov-faint: #94a3b8;
  --ps-ov-accent: #5b39c9;
  --ps-ov-accent-soft: #7c5cea;
  --ps-ov-card-bg: #ffffff;
  --ps-ov-card-border: #e6e8f4;
  --ps-ov-line: #eef0f6;
  --ps-ov-line-strong: #e2e8f0;
  --ps-ov-track: #eef0f6;
  --ps-ov-row-hover: rgba(91, 57, 201, 0.05);
  --ps-ov-thumb-bg: #f1f5f9;
  --ps-ov-control-bg: #ffffff;
  --ps-ov-price: #475569;
  --ps-ov-pill-active-bg: rgba(91, 57, 201, 0.16);
  --ps-ov-pill-active-border: rgba(91, 57, 201, 0.35);
}

.theme--dark .ps-overview {
  --ps-ov-ink: #eef0fa;
  --ps-ov-muted: #9aa1bb;
  --ps-ov-faint: #7c82a0;
  --ps-ov-accent: #9a8cf0;
  --ps-ov-accent-soft: #b3a8f5;
  --ps-ov-card-bg: #1b1d30;
  --ps-ov-card-border: rgba(154, 140, 240, 0.16);
  --ps-ov-line: rgba(148, 154, 184, 0.12);
  --ps-ov-line-strong: rgba(154, 140, 240, 0.22);
  --ps-ov-track: rgba(154, 140, 240, 0.14);
  --ps-ov-row-hover: rgba(154, 140, 240, 0.08);
  --ps-ov-thumb-bg: rgba(154, 140, 240, 0.12);
  --ps-ov-control-bg: #22243a;
  --ps-ov-price: #c3c8de;
  --ps-ov-pill-active-bg: rgba(154, 140, 240, 0.2);
  --ps-ov-pill-active-border: rgba(154, 140, 240, 0.45);
}

/* ---------- Header ---------- */

.ps-overview-header {
  margin-bottom: 8px;
}

.ps-overview-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ps-ov-accent);
  margin-bottom: 4px;
}

.ps-overview-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--ps-ov-ink);
  margin: 0;
  overflow-wrap: anywhere;
  min-width: 0;
}

.ps-year-select {
  max-width: 132px;
  min-width: 110px;
}

.ps-header-controls {
  gap: 10px;
}

.ps-period-toggle,
.ps-sort-toggle {
  border: 1px solid #e2e8f0;
  border-radius: 10px !important;
  background: #f1f2f7 !important;
  padding: 3px;
  height: 38px !important;
  gap: 4px;
  display: inline-flex;
  align-items: center;
  box-shadow: none !important;
}

.ps-period-toggle .v-btn,
.ps-sort-toggle .v-btn {
  text-transform: none;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  color: #64748b;
  background-color: transparent !important;
  border: none !important;
  height: 30px !important;
  padding: 0 12px;
  border-radius: 7px !important;
  box-shadow: none !important;
}

.ps-period-toggle .v-btn.v-btn--active,
.ps-sort-toggle .v-btn.v-btn--active {
  background-color: #ffffff !important;
  color: #5b39c9 !important;
  border: none !important;
  border-radius: 7px !important;
  font-weight: 700 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
}

.ps-overview--dark .ps-period-toggle,
.ps-overview--dark .ps-sort-toggle {
  border-color: rgba(154, 140, 240, 0.16);
  background: #1b1d30 !important;
}

.ps-overview--dark .ps-period-toggle .v-btn,
.ps-overview--dark .ps-sort-toggle .v-btn {
  color: #7c82a0;
}

.ps-overview--dark .ps-period-toggle .v-btn.v-btn--active,
.ps-overview--dark .ps-sort-toggle .v-btn.v-btn--active {
  background-color: rgba(154, 140, 240, 0.18) !important;
  color: #c4b5fd !important;
  box-shadow: none !important;
}

.ps-period-toggle .v-btn.v-btn--active::before,
.ps-sort-toggle .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

.ps-export-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: 10px !important;
  border-color: var(--ps-ov-accent) !important;
  color: var(--ps-ov-accent);
  background: var(--ps-ov-control-bg) !important;
  height: 32px !important;
}

.ps-export-btn .v-icon {
  color: var(--ps-ov-accent);
}

.ps-year-select ::v-deep .v-input__slot {
  border-radius: 12px;
  background: var(--ps-ov-control-bg);
}

.theme--dark .ps-year-select ::v-deep .v-input__slot fieldset {
  border-color: var(--ps-ov-card-border);
  color: var(--ps-ov-ink);
}

.theme--dark .ps-year-select ::v-deep .v-select__selection,
.theme--dark .ps-year-select ::v-deep .v-label,
.theme--dark .ps-year-select ::v-deep .v-icon {
  color: var(--ps-ov-muted) !important;
}

.theme--dark .ps-year-select ::v-deep .v-select__selection {
  color: var(--ps-ov-ink) !important;
}

/* ---------- Shared card chrome (KPI + charts — unchanged) ---------- */

.ps-card-soft {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  transition: box-shadow 0.15s ease;
}

.theme--dark .ps-card-soft {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.ps-kpi-card {
  height: 178px;
}

.ps-kpi-card ::v-deep .v-card__text {
  padding: 16px 18px 14px;
}

.ps-chart-card {
  height: 100%;
}

.ps-chart-card ::v-deep .v-card__text {
  padding: 4px 20px 16px;
}

.ps-kpi-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.ps-kpi-meta {
  min-width: 0;
}

.ps-kpi-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.ps-kpi-desc {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-kpi-value {
  font-size: 22px;
  font-weight: 900;
  color: #141021;
  line-height: 1.15;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-dark-mode-value {
  color: #ffffff;
}

.ps-kpi-spark,
.ps-kpi-spark-placeholder {
  width: 100%;
  height: 24px;
  display: block;
}

.ps-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11.5px;
  color: #64748b;
}

.ps-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ps-legend-dot {
  width: 14px;
  height: 3px;
  border-radius: 2px;
  display: inline-block;
  background: #5b39c9;
}

.ps-legend-dot--revenue {
  height: 0;
  background: none;
  border-top: 2px dashed #94a3b8;
}

.ps-chart-summary {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
}

.ps-chart-summary-value {
  font-size: 20px;
  font-weight: 900;
  color: #141021;
  font-variant-numeric: tabular-nums;
}

.ps-chart-summary-note {
  font-size: 12px;
  color: #94a3b8;
}

.ps-muted-note {
  font-size: 11.5px;
  color: #94a3b8;
}

/* ---------- Best sell card ---------- */

.ps-best-card {
  background: var(--ps-ov-card-bg) !important;
  border: 1px solid var(--ps-ov-card-border) !important;
  border-radius: 18px !important;
}

.ps-best-card .v-card__title {
  padding: 20px 20px 10px;
}

.ps-best-card .v-card__text {
  padding: 4px 20px 16px;
}

.ps-best-head {
  row-gap: 8px;
}

.ps-best-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.005em;
  color: var(--ps-ov-ink);
}

.ps-title-tick {
  width: 4px;
  height: 16px;
  border-radius: 2px;
  background: var(--ps-ov-accent);
  display: inline-block;
  flex: 0 0 4px;
}

.ps-best-note {
  margin-left: 10px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ps-ov-faint);
}

.ps-view-all {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ps-ov-accent);
  white-space: nowrap;
}

.ps-view-all:hover {
  text-decoration: underline;
}

.ps-best-table {
  display: flex;
  flex-direction: column;
}

.ps-best-row {
  display: grid;
  grid-template-columns: 40px 46px minmax(0, 1fr) 132px 160px 150px;
  align-items: center;
  gap: 16px;
  padding: 13px 8px;
  border-bottom: 1px solid var(--ps-ov-line);
  border-radius: 10px;
  transition: background-color 0.15s ease;
}

.ps-best-row:last-child {
  border-bottom: none;
}

.ps-best-row:not(.ps-best-row--head):hover {
  background: var(--ps-ov-row-hover);
}

.ps-best-row--head {
  padding: 0 8px 10px;
  border-bottom: 1px solid var(--ps-ov-line-strong);
  border-radius: 0;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ps-ov-faint);
}

.ps-best-col--right {
  text-align: right;
}

.ps-best-id {
  font-size: 12.5px;
  color: var(--ps-ov-faint);
  font-variant-numeric: tabular-nums;
}

.ps-best-thumb {
  background: var(--ps-ov-thumb-bg);
  border-radius: 10px !important;
}

.ps-best-name-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-best-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-ov-price);
  font-variant-numeric: tabular-nums;
}

.ps-best-total {
  font-size: 13.5px;
  font-variant-numeric: tabular-nums;
  color: var(--ps-ov-accent);
  font-weight: 700;
}

.ps-best-empty {
  padding: 28px 4px;
  text-align: center;
  color: var(--ps-ov-faint);
  font-size: 13px;
}

.ps-product-name {
  font-weight: 700;
  font-size: 13.5px;
  color: var(--ps-ov-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-product-category {
  font-size: 11px;
  color: var(--ps-ov-faint);
}

.ps-units-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
}

.ps-units-bar-track {
  flex: 1;
  max-width: 88px;
  height: 4px;
  border-radius: 999px;
  background: var(--ps-ov-track);
  overflow: hidden;
  display: inline-block;
}

.ps-units-bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    var(--ps-ov-accent-soft),
    var(--ps-ov-accent)
  );
}

.ps-units-value {
  min-width: 26px;
  text-align: right;
  font-weight: 600;
  color: var(--ps-ov-ink);
  font-variant-numeric: tabular-nums;
}

.ps-table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 8px 2px;
  font-size: 11.5px;
  color: var(--ps-ov-faint);
}
</style>
